import { getSalesComparison } from '@lib/api-services/suburbProfileService'
import { useState, useEffect } from 'react'

/**
 * Custom hook to fetch sales comparison data for a suburb with optional filters
 * @param {string} slug - Suburb slug
 * @param {string} type - Type filter ('house', 'unit', or '')
 * @param {string} bedrooms - Bedrooms filter ('Overall', '1', '2', '3', '4', '5+')
 * @param {Object} initialData - Initial sales comparison data from server-side props
 * @returns {Object} { salesComparison, isLoading, error }
 */
function useSalesComparison(
  slug,
  type = 'all',
  bedrooms = 'Overall',
  initialData = null
) {
  const [salesComparison, setSalesComparison] = useState(initialData)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!slug) {
      setSalesComparison(initialData)
      return
    }

    const hasFilters = type || bedrooms
    if (!hasFilters) {
      setSalesComparison(initialData)
      return
    }

    const fetchFilteredData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const params = {}
        if (type) params.type = type
        if (bedrooms) params.bedrooms = bedrooms

        const response = await getSalesComparison(slug, params)
        setSalesComparison(response)
      } catch (err) {
        console.error('Error fetching filtered sales comparison data:', err)
        setError(err)
        if (initialData) {
          setSalesComparison(initialData)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchFilteredData()
  }, [type, bedrooms, slug, initialData])

  useEffect(() => {
    const hasFilters = type || bedrooms
    if (initialData && !hasFilters) {
      setSalesComparison(initialData)
    }
  }, [initialData, type, bedrooms])

  return { salesComparison, isLoading, error }
}

export default useSalesComparison
